import { useState, useEffect, useCallback } from "react";
import {
  BatchStats,
  BatchAdvancedStats,
} from "../../../../pages/playground/types";
import { getStats, getAdvancedStats } from "../../../../api/batches";
import { useAuthStore } from "../../../../authProvider";

export interface UseJobListStatsReturn {
  stats: BatchStats | null;
  totalItems: number;
  advancedStats: BatchAdvancedStats | null;
  reloadStats: () => Promise<void>;
  reloadAdvancedStats: () => Promise<void>;
}

/**
 * Hook to fetch and manage batch statistics
 *
 * Automatically fetches stats when batchId changes
 * Provides methods to manually reload stats
 *
 * @param batchId - The batch ID to fetch stats for (can be undefined)
 * @returns Stats data and reload functions
 */
export function useJobListStats(
  batchId: string | undefined
): UseJobListStatsReturn {
  const { user } = useAuthStore();

  const [stats, setStats] = useState<BatchStats | null>(null);
  const [advancedStats, setAdvancedStats] = useState<BatchAdvancedStats | null>(
    null
  );

  const reloadStats = useCallback(async () => {
    if (batchId) {
      const response = await getStats({ batchId });
      setStats(response.data.data);
    }
  }, [batchId]);

  const reloadAdvancedStats = useCallback(async () => {
    if (user?.isClientRelated) {
      return;
    }

    if (batchId) {
      const response = await getAdvancedStats({ batchId });
      setAdvancedStats(response.data.data);
    }
  }, [batchId, user]);

  useEffect(() => {
    reloadStats();
    reloadAdvancedStats();
  }, [reloadStats, reloadAdvancedStats]);

  return {
    stats,
    totalItems: stats?.total_jobs || 0,
    advancedStats,
    reloadStats,
    reloadAdvancedStats,
  };
}
